import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderNoteModel } from 'src/app/models/shopping/provider-note.model';
import { ProviderInvoiceSelectionTableModel } from 'src/app/models/tables/shopping/provider-invoice-selection-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProviderNoteService {

  private readonly _httpClient = inject(HttpClient);

  changeStateAsync(providerNoteId: number): Observable<ProviderNoteModel> {
    return this._httpClient.put<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.ChangeStateAsync}/${providerNoteId}`,
      null);
  }

  createProviderNote(providerNote: ProviderNoteModel): Observable<ProviderNoteModel> {
    return this._httpClient.post<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.CreateProviderNote}`,
      providerNote);
  }

  deleteProviderNote(providerNoteId: number): Observable<ProviderNoteModel> {
    const params = new HttpParams().set('providerNoteId', providerNoteId);
    return this._httpClient.delete<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.DeleteProviderNote}`,
      { params: params }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetColumnsForGeneralConsultation}`);
  }

  getColumnsForProviderInvoiceAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetColumnsForProviderInvoiceAsync}`);
  }

  getFirstProviderNoteByKeysAsync(branchId: number): Observable<ProviderNoteModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetFirstProviderNoteByKeysAsync}`, { params: params });
  }

  getLastProviderNoteByKeysAsync(branchId: number): Observable<ProviderNoteModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetLastProviderNoteByKeysAsync}`, { params: params });
  }

  getNextProviderNoteByKeysAsync(branchId: number, consecutive: number): Observable<ProviderNoteModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetNextProviderNoteByKeysAsync}`, { params: params });
  }

  getPreviusProviderNoteByKeysAsync(branchId: number, consecutive: number): Observable<ProviderNoteModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetPreviusProviderNoteByKeysAsync}`, { params: params });
  }

  getProviderNoteByKeys(branchId: number, providerId: number, prefix: string | null, noteNumber: number): Observable<ProviderNoteModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('providerId', providerId)
      .set('prefix', prefix || '')
      .set('noteNumber', noteNumber);
    return this._httpClient.get<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetProviderNoteByKeysAsync}`,
      { params: params }
    );
  }

  getProviderNoteById(providerNoteId: number): Observable<ProviderNoteModel> {
    const params = new HttpParams().set('providerNoteId', providerNoteId);
    return this._httpClient.get<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetProviderNoteById}`,
      { params: params }
    );
  }

  getProviderInvoiceAsync(page: number, limit: number, columnSort: string | null, ascendant: boolean, branchId: number, providerId: number, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ProviderInvoiceSelectionTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant)
      .set('branchId', branchId)
      .set('providerId', providerId);
    return this._httpClient.post<PagedResponseModel<ProviderInvoiceSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetProviderInvoiceAsync}`,
      filters, { params: params }
    );
  }

  getProviderNotesForGeneralConsultation(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.GetProviderNotesForGeneralConsultation}`,
      filters, { params: params }
    );
  }

  updateProviderNote(providerNote: ProviderNoteModel): Observable<ProviderNoteModel> {
    return this._httpClient.put<ProviderNoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerNote.UpdateProviderNote}`,
      providerNote);
  }

}
